import { useEffect, useState } from 'react';

/**
 * SSR-safe `matchMedia` subscription. Used by `ResponsivePopover` for the
 * popover ↔ bottom-sheet breakpoint (`(max-width: 719px)` — the "under 720px"
 * line from the prototype's behavioral contract).
 *
 * StrictMode-resilient by construction: the effect holds no lifetime ref that
 * could get stuck across the dev double-invoke (setup→cleanup→setup) — each
 * invocation subscribes fresh and its cleanup unconditionally unsubscribes
 * the exact listener it just added, so running setup→cleanup→setup twice
 * leaves exactly one live listener, same as a single real mount.
 */
export function useMediaQuery(query: string): boolean {
  const getMatch = (): boolean =>
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia(query).matches
      : false;

  const [matches, setMatches] = useState<boolean>(getMatch);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const mql = window.matchMedia(query);
    // Re-sync on every effect run — `query` may have changed since the
    // initial render's `getMatch()` call, and jsdom/browser MediaQueryList
    // state can differ from the lazy-initial-state snapshot by the time this
    // effect runs.
    setMatches(mql.matches);

    const handler = (e: MediaQueryListEvent) => setMatches(e.matches);
    if (typeof mql.addEventListener === 'function') {
      mql.addEventListener('change', handler);
      return () => mql.removeEventListener('change', handler);
    }
    // Safari < 14 fallback (deprecated but still the only API there).
    mql.addListener(handler);
    return () => mql.removeListener(handler);
  }, [query]);

  return matches;
}
